import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from "react-router-dom";
import PropagateLoader from "react-spinners/PropagateLoader";
import { toast } from 'react-toastify';
import { Modal, Card, Typography, Alert } from 'antd';
import { isNumber } from '../../Utils/Common';
import { postRequest } from '../../Utils/AppApi';
import {
    FINO_IMW_CHECK_SENDER,
    FINO_IMW_GET_SENDER_LIMIT,
    FINO_IMW_SENDER_REGISTRATION
} from '../../Utils/appConstants';
import AddCustomer from './addCustomer';
import ViewCustomer from './viewCustomer';

const { Title, Text, Paragraph } = Typography;

const primaryRed = '#C9262C';
const primaryOrange = '#FF8C00';
const primaryGreen = '#02A650';
const cardBorder = '#E5E7EB';

const KYC_DISABLED_MESSAGE =
    "Customer KYC is not completed, and KYC services are currently disabled.";

const FinoImWDMT = (props) => {
    const history = useHistory();
    const [isLoading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isCustomerInfo, setCustomerInfo] = useState(null);
    const [isCustomerNotExist, setCustomerNotExist] = useState(false);
    const [ineligibleReason, setIneligibleReason] = useState(null);
    const [isOtpModal, setOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState(null);
    const [otpReference, setOtpReference] = useState(null);
    const [kycRequestID, setKycRequestID] = useState(null);
    const [isProcessing, setProcessing] = useState(false);

    const mobileRef = useRef(searchText);
    const cardRef = useRef(null);

    useEffect(() => {
        mobileRef.current = searchText;
    }, [searchText]);

    useEffect(() => {
        if (props.mobile) {
            setSearchText(props.mobile);
            setTimeout(() => {
                getCustomerInfo();
            }, 300);
        }
    }, [props.mobile]);

    useEffect(() => {
        if (searchText && searchText.length === 10) {
            const timer = setTimeout(() => {
                getCustomerInfo();
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [searchText]);

    useEffect(() => {
        if (isCustomerInfo || isCustomerNotExist) {
            scrollToCard();
        }
    }, [isCustomerInfo, isCustomerNotExist, ineligibleReason]);

    useEffect(() => {
        if (isCustomerNotExist && !ineligibleReason) {
            toast.error(KYC_DISABLED_MESSAGE);
        }
    }, [isCustomerNotExist, ineligibleReason]);

    const scrollToCard = () => {
        if (cardRef.current) {
            cardRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    };

    const getCustomerInfo = async () => {
        if (isProcessing) return;
        const mobile = mobileRef.current;

        if (!mobile) {
            toast.error("Customer Mobile Number is required");
            return;
        }
        if (mobile.length !== 10) {
            toast.error("Please enter a valid 10-digit Mobile Number");
            return;
        }

        setLoading(true);
        setIneligibleReason(null);
        const payload = { senderMobile: mobile };

        try {
            const response = await postRequest(FINO_IMW_CHECK_SENDER, payload, history);

            if (response.success === true) {
                if (response.isRegistered === true) {
                    await getSenderLimit(mobile);
                } else {
                    setCustomerInfo(null);
                    setCustomerNotExist(true);
                    setKycRequestID(null);
                }
            } else if (response.message?.includes("Customer Have Other Account with bank")) {
                setIneligibleReason(response.message);
                setCustomerNotExist(false);
                toast.error(response.message);
            } else if (response.isEKYCNotDone === true) {
                setCustomerInfo(null);
                setCustomerNotExist(true);
                setKycRequestID(null);
                toast.info("eKYC is pending. KYC services are currently disabled.");
            } else {
                toast.error(response.message || "Failed to fetch customer info");
                setCustomerNotExist(false);
            }
        } catch (error) {
            console.error(error);
            toast.error("Error fetching customer info");
            setCustomerNotExist(false);
        } finally {
            setLoading(false);
        }
    };

    const getSenderLimit = async (mobile) => {
        if (isProcessing) return;
        const payload = { senderMobile: mobile };
        try {
            const response = await postRequest(FINO_IMW_GET_SENDER_LIMIT, payload, history);
            if (response.success === true) {
                setCustomerInfo({
                    customerMobile: mobile,
                    ...response.response
                });
                setCustomerNotExist(false);
            } else {
                toast.error(response.message || "Failed to fetch limit");
            }
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    // COMMENTED: OTP flow is disabled along with KYC services.
    // const handleOtpVerify = () => {
    //     if (!otpCode) {
    //         toast.error("OTP code is required");
    //         return;
    //     }
    //     if (isProcessing) return;
    //
    //     setLoading(true);
    //     setProcessing(true);
    //     const payload = {
    //         senderMobile: mobileRef.current,
    //         OTPRequestId: otpReference,
    //         OTPPin: otpCode
    //     };
    //
    //     postRequest(FINO_IMW_SENDER_REGISTRATION, payload, history)
    //         .then((response) => {
    //             if (response.success === true) {
    //                 setOtpModal(false);
    //                 setCustomerInfo({
    //                     customerMobile: mobileRef.current,
    //                     ekycId: response.data.EkycID,
    //                     isRegistered: true
    //                 });
    //                 setCustomerNotExist(false);
    //                 toast.success(response.message || "Registration Successful");
    //                 getSenderLimit(mobileRef.current);
    //             } else {
    //                 toast.error(response.message || "Registration Failed");
    //             }
    //             setLoading(false);
    //             setProcessing(false);
    //         })
    //         .catch((error) => {
    //             console.error(error);
    //             setLoading(false);
    //             setProcessing(false);
    //             toast.error("Registration failed");
    //         });
    // };

    // COMMENTED: OTP change handler
    // const onChangeOtpValue = (value) => {
    //     setOtpCode(value);
    // };

    if (isLoading) {
        return (
            <div
                style={{
                    minHeight: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(160deg, #f8f9fc 0%, #eef1f8 40%, #f5f3ff 100%)',
                }}
            >
                <PropagateLoader color={primaryRed} loading={isLoading} size={22} />
            </div>
        );
    }

    return (
        <section
            style={{
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div style={{ position: 'absolute', top: -80, right: -80, width: 350, height: 350, background: 'radial-gradient(circle, rgba(255,221,9,0.15) 0%, transparent 70%)', borderRadius: '50%' }} />
            <div style={{ position: 'absolute', bottom: -40, left: -40, width: 250, height: 250, background: 'radial-gradient(circle, rgba(201,38,44,0.08) 0%, transparent 70%)', borderRadius: '50%' }} />

            <div style={{ margin: '0 auto', padding: '0 20px', position: 'relative', zIndex: 2 }}>
                {ineligibleReason && (
                    <Alert
                        message={<strong>This Customer is not eligible to do FINO DMT</strong>}
                        description={ineligibleReason}
                        type="error"
                        showIcon
                        style={{ marginBottom: 20, borderRadius: 12 }}
                    />
                )}

                <Card
                    ref={cardRef}
                    bordered={false}
                    style={{
                        borderRadius: 18,
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04), 0 12px 28px -4px rgba(0,0,0,0.07)',
                        background: '#ffffff',
                        border: '1px solid rgba(0,0,0,0.03)',
                        width: '100%',
                    }}
                    bodyStyle={{ padding: '20px 24px' }}
                >
                    {/* COMMENTED: KYC / AddCustomer flow disabled.
                        Customer KYC is not completed, and KYC services are currently disabled.
                        Instead of navigating to AddCustomer, we show the message below.

                    {(isCustomerNotExist && !ineligibleReason) && (
                        <AddCustomer
                            mobile={searchText}
                            findCustomerInfo={getCustomerInfo}
                            setOtpModal={setOtpModal}
                            setOtpReference={setOtpReference}
                            setKycRequestID={setKycRequestID}
                            isProcessing={isProcessing}
                            setProcessing={setProcessing}
                        />
                    )}
                    */}

                    {(isCustomerNotExist && !ineligibleReason) && (
                        <Alert
                            message={<strong>Customer KYC is not completed</strong>}
                            description={KYC_DISABLED_MESSAGE}
                            type="warning"
                            showIcon
                            style={{ borderRadius: 12 }}
                        />
                    )}

                    {(isCustomerInfo && !isCustomerNotExist) && (
                        <div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'left',
                                    gap: 10,
                                    marginBottom: 18,
                                    paddingBottom: 12,
                                    borderBottom: '2px solid #ede9fe',
                                }}
                            >
                                <div
                                    style={{
                                        width: 6,
                                        height: 28,
                                        background: primaryRed,
                                        borderRadius: 4,
                                    }}
                                />
                                <Text strong style={{ fontSize: 17, color: '#1A1A2E' }}>
                                    Beneficiary List
                                </Text>
                                <Text type="secondary" style={{ fontSize: 13, marginLeft: 'auto' }}>
                                    {isCustomerInfo?.customerMobile && `📱 ${isCustomerInfo.customerMobile}`}
                                </Text>
                            </div>
                            <ViewCustomer
                                isCustomerInfo={isCustomerInfo}
                                findCustomerInfo={getCustomerInfo}
                            />
                        </div>
                    )}

                    {!isCustomerInfo && !isCustomerNotExist && props.mobile && (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <Text type="secondary">Loading customer details…</Text>
                        </div>
                    )}
                </Card>
            </div>

            {/* COMMENTED: OTP modal disabled along with KYC services.
            {isOtpModal && (
                <Modal
                    title={
                        <span style={{ color: '#1A1A2E', fontWeight: 700 }}>
                            <i className="fa fa-shield" style={{ color: primaryRed, marginRight: '8px' }} />
                            Customer Verification OTP
                        </span>
                    }
                    width={500}
                    open={isOtpModal}
                    onOk={handleOtpVerify}
                    onCancel={() => setOtpModal(false)}
                    okText="Verify"
                    cancelText="Cancel"
                    maskClosable={false}
                    keyboard={false}
                    confirmLoading={isLoading}
                    style={{ borderRadius: 16 }}
                    bodyStyle={{ padding: '24px 30px' }}
                    okButtonProps={{
                        style: {
                            background: primaryRed,
                            borderColor: primaryRed,
                            borderRadius: 8,
                            fontWeight: 600,
                        }
                    }}
                    cancelButtonProps={{
                        style: {
                            borderRadius: 8,
                            borderColor: '#d9d9d9',
                            color: '#6B7280',
                        }
                    }}
                >
                    <div style={{ marginBottom: 8 }}>
                        <label style={{ fontWeight: 600, color: '#1A1A2E', display: 'block', marginBottom: 6 }}>
                            OTP Code
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter 4-digit OTP"
                            onChange={(e) => onChangeOtpValue(e.target.value)}
                            autoComplete="off"
                            value={otpCode || ''}
                            maxLength={4}
                            onKeyDown={isNumber}
                            disabled={isProcessing}
                            style={{
                                borderRadius: 10,
                                border: `1px solid ${cardBorder}`,
                                padding: '12px 16px',
                                transition: 'border 0.3s, box-shadow 0.3s',
                                width: '100%',
                                fontSize: 16,
                                outline: 'none',
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = primaryRed;
                                e.target.style.boxShadow = `0 0 0 3px rgba(201,38,44,0.12)`;
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = cardBorder;
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        <small style={{ color: '#6B7280', marginTop: 4, display: 'block' }}>
                            Enter the 4‑digit OTP sent to the customer's mobile
                        </small>
                    </div>
                </Modal>
            )}
            */}
        </section>
    );
};

export default FinoImWDMT;